import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HandleErrorService } from "@app/core/services/handle-error.service";
import { HttppPaginateResponse } from "@app/flow/core/models/http-paginate-flow";
import {
	TemplateRequest,
	UseCaseColumn,
	UseCaseGroup,
	UseCaseGroupBySlug,
	UseCaseGroupRequest,
	UseCaseTemplate,
} from "@app/flow/core/models/use-case.model";
import { UseCaseReport } from "@app/flow/features/dashboard-flow/types/report.model";
import { HotToastService } from "@ngneat/hot-toast";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment.ipt.local";

@Injectable({
	providedIn: "root",
})
export class ApiUseCaseGroupService {
	private basePath = `${environment.urlBackendFlujoBajas}/v1/use-cases`;

	constructor(
		private http: HttpClient,
		private _toastService: HotToastService,
		private _handleErrorService: HandleErrorService
	) {}

	create(request: UseCaseGroupRequest) {
		return this.http.post<any>(`${this.basePath}/group`, request);
	}

	update(useCaseGroupId: number, request: UseCaseGroupRequest) {
		return this.http.put<any>(
			`${this.basePath}/group/${useCaseGroupId}`,
			request
		);
	}

	delete(useCaseGroupId: number) {
		return this.http.delete(`${this.basePath}/group/${useCaseGroupId}`);
	}

	findOne(useCaseGroupId: number): Observable<UseCaseGroup> {
		return this.http.get<UseCaseGroup>(
			`${this.basePath}/group/${useCaseGroupId}`
		);
	}

	findAll(): Observable<HttppPaginateResponse<UseCaseGroup>> {
		return this.http.get<HttppPaginateResponse<UseCaseGroup>>(
			`${this.basePath}/group/`
		);
	}

	findBySlug(slug: string): Observable<UseCaseGroupBySlug> {
		return this.http
			.get<UseCaseGroupBySlug>(`${this.basePath}/by/${slug}`)
			.pipe(map((response) => this.mapToUseCaseGroup(response)));
	}

	getColumns(
		groupId: number,
		useCaseId?: number
	): Observable<{
		all: UseCaseColumn[];
		default: UseCaseColumn[];
	}> {
		return this.http
			.get<{
				all: UseCaseColumn[];
				default: UseCaseColumn[];
			}>(
				`${this.basePath}/${groupId}/columns${
					useCaseId ? "?use_case_id=" + useCaseId : ""
				}`
			)
			.pipe(
				map((res) => {
					return {
						all: this.addCallableProperty(res.all),
						default: this.addCallableProperty(res.default),
					};
				})
			);
	}

	private mapToUseCaseGroup(
		useCaseGroup: UseCaseGroupBySlug
	): UseCaseGroupBySlug {
		useCaseGroup.use_cases = useCaseGroup.use_cases.map((useCase) => {
			if (typeof useCase?.roles === "string") {
				useCase.roles = (useCase.roles as string)
					.split(",")
					.map((role) => role.trim().toUpperCase())
					.filter(Boolean);
			} else {
				useCase.roles = [];
			}
			return useCase;
		});
		return useCaseGroup;
	}

	private addCallableProperty(cols: UseCaseColumn[]) {
		return cols.map((be) => ({
			...be,
			callable: be.callable ?? true,
		}));
	}

	// to filters columns
	getTemplates(useCaseId: number): Observable<UseCaseTemplate[]> {
		return this.http
			.get<UseCaseTemplate[]>(`${this.basePath}/${useCaseId}/filters`)
			.pipe(
				map((cols) => {
					const parse = cols.map((col) => {
						return {
							...col,
							filters: col.filters.map((f) => ({
								...f,
								callable: f.callable ?? true,
							})),
						};
					});

					return parse;
				})
			);
	}

	createTemplate(useCaseId: number, body: TemplateRequest): Observable<any> {
		return this.http.post<any>(
			`${this.basePath}/${useCaseId}/filters`,
			body
		);
	}

	deleteTemplate(templateId: number, name?: string): Observable<any> {
		return this.http.delete(`${this.basePath}/${templateId}/filters`).pipe(
			tap(() =>
				this._toastService.success(
					`Template ${name.toUpperCase()} eliminado`,
					{
						dismissible: true,
					}
				)
			),
			catchError((e) => this._handleErrorService.handleError(e))
		);
	}

	getReports(slug: string): Observable<UseCaseReport[]> {
		// return of<UseCaseReport[]>(MOCK_TO_SELECT_REPORT);
		return this.http.get<UseCaseReport[]>(
			`${this.basePath}/${slug}/reports`
		);
	}
}
